import { Typography } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { memo, useRef } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { DeferredResponseFormat } from '../../../types/J1QL';
import { useExpandable } from '../../hooks';
import { JupiterTheme } from '../../theme/types';
import QueryToolbarActions from './QueryToolbarActions';
import { ResultFormat } from './types';
import { ToolbarItemDescriptor } from './util/toolbarItemDescriptors';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    title: {
      color: theme.palette.grey[500],
      fontSize: 14,
      lineHeight: 1.85,
      letterSpacing: 1.1,
      fontWeight: 700,
      marginLeft: theme.spacing(1),
    },
    query: {
      color: theme.palette.text.primary,
      flexGrow: 1,
      fontFamily: `'Ubuntu Mono', monospace`,
      fontWeight: 'normal',
      fontSize: 16,
      lineHeight: 1.3,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      wordBreak: 'break-word',
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(2),
      marginTop: theme.spacing(0.375),
    },
    queryExpanded: {
      whiteSpace: 'pre-wrap',
      textOverflow: 'none',
      overflow: 'none',
    },
    resultsHeader: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      transition: '.3s cubic-bezier(0.2, 0.4, 0.8, 1)',
      padding: theme.spacing(2, 2, 0, 2),
    },
    resultsHeaderExpanded: {
      // In order for the transition to work, a maxHeight value must be set,
      // so set this to something that is way bigger than we will ever grow to
      maxHeight: 5000,
      paddingBottom: theme.spacing(2),
    },
    iconsContainer: {
      display: 'flex',
    },
  })
);

interface Props {
  savable?: boolean;
  onRemoveResult?: VoidFunction;
  selectedFormat?: ResultFormat;
  onSelectedFormat?: (format: ResultFormat) => void;
  onSelectedDownload?: (format: DeferredResponseFormat) => void;
  isExporting?: boolean;
  exportable?: boolean;
  query: string;
  toolbarIcons?: ToolbarItemDescriptor[];
  addToInsights?: boolean;
  hideAlert?: boolean;
  hasResults?: boolean;
}

export const QueryToolbar = ({ query, ...props }: Props) => {
  const classes = useStyles();
  const queryExpanded = useBooleanState(false);

  const ref = useRef<HTMLElement>(null);
  const queryExpandable = useExpandable({ ref });

  return (
    <>
      <div
        className={clsx(
          classes.resultsHeader,
          queryExpanded.state && classes.resultsHeaderExpanded
        )}
      >
        <Typography className={classes.title}>QUERY</Typography>
        <Typography
          ref={ref}
          variant="h6"
          className={clsx(
            classes.query,
            queryExpanded.state && classes.queryExpanded
          )}
        >
          {query}
        </Typography>
        <div className={classes.iconsContainer}>
          <QueryToolbarActions
            query={query}
            expandable={queryExpandable}
            expanded={queryExpanded.state}
            onToggleExpanded={queryExpanded.toggle}
            {...props}
          />
        </div>
      </div>
    </>
  );
};

export default memo(QueryToolbar);
